<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <card-title-row translation-path="sidebar.unassignedWatermeters.title" class="pb-5" :has-buttons="false">
        <template #subtitle>
          <italic-subheader
            style="font-size: 12px !important;"
            class="text-left"
            translation-path="sidebar.unassignedWatermeters.subtitle"
          />
        </template>
      </card-title-row>
      <sidebar-row class="pb-5">
        <v-row no-gutters class="items-center" style="gap: 0.5rem">
          <v-col cols="auto">
            <data-input
              v-model="sortType"
              v-bind="sortTypeInput"
              @change="changeSorting"
              class="table-select"
              :class="sortType ? 'table-select-selected' : 'table-select-empty'"
            >
              <template #selection></template>
              <template #prepend-inner>
                <span
                  class="table-select-text"
                  :class="!sortType ? 'inactive-table-select-text' : 'active-table-select-text'"
                >
                  <v-icon>
                    mdi-swap-vertical
                  </v-icon>
                  {{ $i18n.t('sidebar.unassignedWatermeters.sorting') }}
                </span>
              </template>
            </data-input>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-row no-gutters style="gap: 0.4rem">
              <v-col style="max-width: 200px">
                <data-input v-model="searchInput" v-bind="searchTypeInput" />
              </v-col>
              <v-col cols="auto" style="display: flex" class="align-center">
                <base-rounded-button
                  icon="$csv"
                  size="x-small"
                  color="success"
                  :disabled="!items.length"
                  @click="saveToCsv"
                  :tooltip="items.length ? $i18n.t('sidebar.unassignedWatermeters.exportToCsv') : null"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row v-if="items.length">
        <ribbon-list
          :listItems="items"
          item-title-key="displayValue"
          class="pb-7"
          :toggle-max-items="10"
          no-flex
          :right-cols="null"
          pagination-type="pages"
        >
          <template #itemButtons-append="{item, index}">
            <v-row no-gutters align="center" class="flex-nowrap">
              <v-col cols="auto" class="flex-shrink-1">
                <v-row no-gutters>
                  <v-col cols="auto" v-for="(value, idx) of item.additionalDisplayValues" :key="idx" class="pr-1 py-1">
                    <v-tooltip bottom color="font">
                      <template #activator="{ on }">
                        <v-chip color="primary" label small v-on="on" style="height: 20px">
                          <div class="white--text list-chip-text">
                            {{ value }}
                          </div>
                        </v-chip>
                      </template>
                      {{ value }}
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-col>
              <v-spacer />
              <v-col cols="auto" class="text-right">
                <data-input
                  simple
                  :ref="`feature-to-add-input-${index}`"
                  :data-type="{ name: 'features' }"
                  :featuresSettings="featuresSettings"
                  :buttonProps="{ isIcon: true, icon: 'mdi-cursor-pointer', iconSize: '18' }"
                  translation-path=""
                  :disabled="activeButtonIdx || activeButtonIdx === 0 ? activeButtonIdx !== index : false"
                  @pushFeatures="openAssignFeatureDialog($event, item)"
                  @status="onButtonStatus($event, index)"
                />
              </v-col>
            </v-row>
          </template>
        </ribbon-list>
      </sidebar-row>
      <main-loader v-else-if="!isFetched" class="pt-5" />
    </v-col>
    <main-dialog
      v-if="isConfirmAssignFeatureLoaded"
      :is-visible.sync="isConfirmAssignFeatureVisible"
      :title="$i18n.t('sidebar.unassignedWatermeters.assignWatermeter')"
      :body="
        $i18n.t('sidebar.unassignedWatermeters.confirmAssignWatermeters', {
          watermeterId: payload.watermeterId,
          partitionId: payload.partitionId,
        })
      "
      :loading="isConfirmAssignFeatureLoading"
      agree-text="dialog.agreeAssign"
      @agree="assignFeature"
    />
  </v-row>
</template>
<script>
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';

import { call, get, sync } from 'vuex-pathify';
export default {
  name: 'UnassignedWatermeters',
  components: {
    CardTitleRow,
    DataInput,
    ItalicSubheader,
    MainLoader,
    RibbonList,
    SidebarRow,
    MainDialog: () => import('@/components/MainDialog'),
  },
  computed: {
    currentTab: sync('sidebar/currentTab'),
    project: sync('layers/project'),
    projects: get('layers/projects'),
    displayIdAttribute: get(
      'admin/modulesMapping@unassigned_watermeters.unassigned_watermeters_datasource.settings.display_id_attribute'
    ),
    displayAttributes: get(
      'admin/modulesMapping@unassigned_watermeters.unassigned_watermeters_datasource.settings.display_attributes'
    ),
    partitionsLayerId: get(
      'admin/modulesMapping@unassigned_watermeters.unassigned_watermeters_partitions_datasource.settings.layer_id'
    ),
    watermetersLayerId: get(
      'admin/modulesMapping@unassigned_watermeters.unassigned_watermeters_datasource.settings.layer_id'
    ),
    watermetersDataSource: get(
      'admin/modulesMapping@unassigned_watermeters.unassigned_watermeters_datasource.datasource'
    ),
    watermeterBillingIdAttributeName: get(
      'admin/modulesMapping@billing.watermeters_datasource_attributes_mapping.id_attribute_name'
    ),
    sortItems() {
      return [
        {
          name: `${this.displayIdAttribute} ${this.$i18n.t('sidebar.unassignedWatermeters.increasing')}`,
          attr: this.displayIdAttribute,
          sortOrder: 'asc',
        },
        {
          name: `${this.displayIdAttribute} ${this.$i18n.t('sidebar.unassignedWatermeters.decreasing')}`,
          attr: this.displayIdAttribute,
          sortOrder: 'desc',
        },
        ...this.displayAttributes
          .map((attr, index) => {
            return [
              {
                name: `${attr} ${this.$i18n.t('sidebar.unassignedWatermeters.increasing')}`,
                attr: attr,
                sortOrder: 'asc',
                attrIndex: index,
              },
              {
                name: `${attr} ${this.$i18n.t('sidebar.unassignedWatermeters.decreasing')}`,
                attr: attr,
                attrIndex: index,
                sortOrder: 'desc',
              },
            ];
          })
          .flat(),
      ];
    },
    sortTypeInput() {
      return {
        itemText: 'name',
        dataType: {
          name: 'select',
        },
        returnObject: true,
        hideDetails: true,
        autoConvertSelect: false,
        clearable: true,
        items: this.sortItems || [],
      };
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
    unassignedWatermetersProjectId: get('admin/settingsValues@unassigned_watermeters_project'),
    featuresSettings() {
      return {
        layers: {
          included: [this.partitionsLayerId],
        },
      };
    },
  },
  data: () => ({
    activeButtonIdx: null,
    isFetched: false,
    items: [],
    payload: {},
    searchInput: '',
    sortType: null,
    isConfirmAssignFeatureLoaded: false,
    isConfirmAssignFeatureVisible: false,
    isConfirmAssignFeatureLoading: false,
  }),
  watch: {
    searchInput(nV) {
      this.items = this.getFilteredItems(nV);
      if (this.sortType) this.changeSorting();
    },
  },
  methods: {
    asignPartitionWatermeter: call('billings/asignPartitionWatermeter'),
    getUnassignedWatermeters: call('billings/getUnassignedWatermeters'),
    openAssignFeatureDialog(v, item) {
      this.payload = { watermeterId: item.billingIdValue, partitionId: v[0].id };
      this.isConfirmAssignFeatureLoaded = true;
      this.isConfirmAssignFeatureVisible = true;
    },
    saveToCsv() {
      const mappedItems = this.items.map(item => [
        item.displayValue,
        ...this.displayAttributes.map(header => item.displayAttributesValuesObject[header]),
      ]);
      const fileContent = `${this.displayIdAttribute};${this.displayAttributes.join(';')}\n${mappedItems
        .map(item => item.join(';'))
        .join('\n')}`;

      const file = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });
      this.$_saveFile(URL.createObjectURL(file), `${this.$i18n.t('sidebar.unassignedWatermeters.title')}.csv`);
    },
    getFilteredItems(text = this.searchInput) {
      let filteredArray = [];
      if (text) {
        const loweredInputValue = text.toLowerCase();
        filteredArray = [...this.itemsProxy].filter(
          item =>
            item.displayValue?.toLowerCase().includes(loweredInputValue) ||
            item.additionalDisplayValues.some(val => val.toLowerCase().includes(loweredInputValue))
        );
      } else {
        filteredArray = [...this.itemsProxy];
      }
      return filteredArray;
    },
    changeSorting(e = this.sortType) {
      if (!e) {
        this.items = [...(this.searchInput ? this.getFilteredItems() : this.itemsProxy)];
        return;
      }
      this.items = [...this.items].sort((a, b) => {
        if (e.sortOrder === 'desc') [a, b] = [b, a];
        const aValue = e.attr === this.displayIdAttribute ? a.displayValue : a.additionalDisplayValues[e.attrIndex];
        const bValue = e.attr === this.displayIdAttribute ? b.displayValue : b.additionalDisplayValues[e.attrIndex];
        switch (typeof aValue && typeof bValue) {
          case 'number':
            return aValue - bValue;
          default:
            return (aValue ?? '').localeCompare(bValue ?? '');
        }
      });
    },
    async assignFeature() {
      this.isConfirmAssignFeatureLoading = true;
      try {
        await this.asignPartitionWatermeter(this.payload);
        this.$root.$emit('refreshMvtSource', this.partitionsLayerId);
        this.$root.$emit('refreshMvtSource', this.watermetersLayerId);
        this.$root.$emit('refreshTableData');
        this.$root.$emit('updateLegendStylesCounts', { layersIds: [this.partitionsLayerId, this.watermetersLayerId] });
        await this.init();
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successAssignWatermeter'),
        });
      } finally {
        this.payload = {};
        this.isConfirmAssignFeatureLoading = false;
        this.isConfirmAssignFeatureLoaded = false;
        this.isConfirmAssignFeatureVisible = false;
      }
      this.$refs[`feature-to-add-input-${this.activeButtonIdx}`]?.$refs['data-input']?.toggleOff();
      this.activeButtonIdx = null;
    },
    onButtonStatus(status, idx) {
      this.activeButtonIdx = status ? idx : null;
    },
    async init() {
      if (this.$route.query.project + '' !== this.unassignedWatermetersProjectId + '') {
        this.project = this.projects[this.unassignedWatermetersProjectId];
        this.$root.$emit('fetchProject');
      }
      const r = await this.getUnassignedWatermeters();
      this.currentTab = 1;
      const displayAttributeName = this.displayIdAttribute;
      const items = r.data.data.map(item => {
        const displayAttributesValuesObject = {};
        const additionalDisplayValues = [];
        const { [displayAttributeName]: displayValue, [this.watermeterBillingIdAttributeName]: billingIdValue } = item;
        for (const attribute of this.displayAttributes) {
          const attributeValue = (item[attribute] || '').toString().trim();
          if (attributeValue || attributeValue === 0 || attributeValue === false) {
            additionalDisplayValues.push(attributeValue);
          }
          displayAttributesValuesObject[attribute] = attributeValue;
        }
        return {
          billingIdValue,
          displayValue,
          additionalDisplayValues,
          displayAttributesValuesObject,
          titleClasses: ['font-weight-medium'],
        };
      });
      this.itemsProxy = Object.freeze(items);
      this.items = this.getFilteredItems();
      this.changeSorting();
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>
<style scoped lang="scss">
.list-chip-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.table-select-selected ::v-deep .v-input {
  background-color: #e3effa;
  .v-input__icon .v-icon {
    color: #1976d2 !important;
  }
}
.table-select-empty ::v-deep .v-input {
  background-color: rgba(207, 207, 207, 0.15);
  .v-input__icon .v-icon {
    color: rgb(48, 48, 48) !important;
  }
}
.table-select-empty ::v-deep fieldset {
  border-color: #e3effa;
}

.table-select-selected ::v-deep fieldset {
  border-color: #1976d2;
}

.table-select ::v-deep .v-input__prepend-inner {
  display: block;
  margin-right: 0.5rem !important;
  margin-top: auto !important;
  margin-bottom: auto !important;
}
.table-select {
  border-radius: 4px;
  .table-select-text {
    display: flex;
    align-items: center;
    margin-top: auto;
    margin-bottom: auto;
    white-space: nowrap;
  }
  .active-table-select-text {
    color: #1976d2;
  }
  .inactive-table-select-text {
    color: rgb(48, 48, 48);
  }
}
</style>
